import { useEffect, useState } from "react";
import { useFormik, Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import VerifyOtpModal from "../../components/Modals/VerifyOtpModal";
import { stellerLogin, loginOtpVerification } from "../../services/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input, Modal } from "antd";
import { Mail, Lock, LogIn, Shield } from "lucide-react";

// Validation schema using Yup
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(4, "Password must be at least 6 characters")
    .required("Password is required"),
});

// Initial form values
const initialValues = {
  email: "",
  password: "",
};

export default function Login() {
  const navigate = useNavigate();
  const [verifyOtpModal, setVerifyOtpModal] = useState(false);
  const [otpExpiryTime, setOtpExpiryTime] = useState(null);
  const session = localStorage.getItem("session");
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
  });
  const token = localStorage.getItem("token");

  const handleSubmit = async (values, { setSubmitting }) => {
    formik.setFieldValue("email", values?.email);
    formik.setFieldValue("password", values?.password);

    const payload = {
      email: values?.email,
      password: values?.password,
    };

    const loginResponse = await stellerLogin(payload);
    const { status, data } = loginResponse || {};

    if (status === 200) {
      setOtpExpiryTime(data?.time);
      setVerifyOtpModal(true);
    } else {
      toast.error(data?.message || "Something went wrong, try again later");
    }
  };

  const handleOtp = async (otp) => {
    const submitOtpResponse = await loginOtpVerification({
      email: formik?.values?.email,
      otp,
    });
    const { status, data } = submitOtpResponse;
    if (status === 200) {
      localStorage.setItem("token", data?.token);
      localStorage.setItem("role", data?.role);
      navigate("/admin");
    }
  };

  useEffect(() => {
    if (token && token !== "" && session !== "expired") {
      navigate("/admin");
    }
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-gradient-to-br from-emerald-200/30 to-teal-200/30 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-gradient-to-tr from-green-200/30 to-emerald-200/30 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-gradient-to-r from-teal-100/20 to-emerald-100/20 blur-3xl"></div>
        </div>

        {/* Main login card */}
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-10">
            {/* Logo and header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#619269] to-[#4a7c59] rounded-2xl mb-6 shadow-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Steller Login
              </h1>
            </div>

            {/* Formik form */}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="space-y-6">
                  {/* Email field */}
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-gray-700 tracking-wide"
                    >
                      Email
                    </label>
                    <div className="relative">
                      <Field
                        id="email"
                        name="email"
                        as={Input}
                        type="email"
                        placeholder="Enter your email"
                        className={`w-full py-3 bg-gray-50/50 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#619269]/20 focus:border-[#619269] transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                          errors.email && touched.email
                            ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                            : "border-gray-200"
                        }`}
                        prefix={<Mail className="h-5 w-5 text-gray-400" />}
                      />
                    </div>
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  {/* Password field */}
                  <div className="space-y-2">
                    <label
                      htmlFor="password"
                      className="block text-sm font-semibold text-gray-700 tracking-wide"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <Field
                        id="password"
                        name="password"
                        as={Input.Password}
                        placeholder="Enter your password"
                        className={`w-full py-3 bg-gray-50/50 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#619269]/20 focus:border-[#619269] transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                          errors.password && touched.password
                            ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                            : "border-gray-200"
                        }`}
                        prefix={<Lock className="h-5 w-5 text-gray-400" />}
                      />
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  {/* Sign in button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#619269] to-[#4a7c59] text-white font-semibold py-4 px-6 rounded-2xl hover:from-[#4a7c59] hover:to-[#3d6b4a] focus:outline-none focus:ring-2 focus:ring-[#619269]/20 focus:ring-offset-2 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        <span>Sign In</span>
                      </>
                    )}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {/* Footer */}
        <footer className="absolute bottom-0 left-0 right-0 bg-slate-800/90 backdrop-blur-sm text-white py-4 z-10">
          <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p className="text-sm text-gray-300">
              Copyright © 2025{" "}
              <span className="text-white font-semibold">The Techtonic</span>
            </p>
            <div className="flex space-x-6 text-sm">
              <a
                href="#"
                className="text-gray-300 hover:text-white transition-colors duration-200"
              >
                About Us
              </a>
              <a
                href="#"
                className="text-gray-300 hover:text-white transition-colors duration-200"
              >
                Blog
              </a>
              <a
                href="#"
                className="text-gray-300 hover:text-white transition-colors duration-200"
              >
                MIT License
              </a>
            </div>
          </div>
        </footer>
      </div>

      <Modal
        open={verifyOtpModal}
        onCancel={() => setVerifyOtpModal(false)}
        footer={null}
        centered
        width={450}
        className="modern-modal"
        wrapClassName="modern-modal-wrapper"
        styles={{
          content: {
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            border: "none",
          },
          mask: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
          },
        }}
        closeIcon={
          <div className="text-gray-500 hover:text-[#619269] transition-colors duration-200">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        }
      >
        <div className="flex flex-col items-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Verify OTP
          </h2>
          <p className="text-gray-500 mb-6 text-center">
            Please enter the One-Time Password (OTP) sent to your registered
            email address. The OTP is valid for the next 10 minutes.
          </p>
          <VerifyOtpModal
            handleOtp={handleOtp}
            closeModal={setVerifyOtpModal}
            email={formik.values.email}
          />
        </div>
      </Modal>
    </>
  );
}
